// src/components/ActivityCenter.tsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Chip,
  Avatar,
  IconButton,
  Grid,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Stack,
  Tooltip,
  Paper,
  Tabs,
  Tab,
  alpha,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  Skeleton,
  Badge,
  Collapse,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Assessment as ReportIcon,
  Schedule as ScheduleIcon,
  TableChart as CsvIcon,
  ViewList as ListViewIcon,
  Timeline as TimelineIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Visibility as VisibilityIcon,
  TrendingUp as TrendingUpIcon,
  ArrowForward as ArrowForwardIcon,
  Today as TodayIcon,
  DateRange as DateRangeIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
} from "@mui/icons-material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
  Area,
  AreaChart,
} from "recharts";
import { gradients, shadows } from "../theme/theme";
import { supabase } from "../lib/supabase";
import PageHeaderTitle from "./PageHeaderTitle";
import PageRefreshButton from "./PageRefreshButton";

// ==================== Types ====================

type ActivityType =
  | "client_created"
  | "client_updated"
  | "client_deleted"
  | "search_executed"
  | "search_saved"
  | "email_sent"
  | "emailed"
  | "sent_text"
  | "left_voicemail"
  | "guest_card_sent"
  | "guest_card_viewed"
  | "property_viewed"
  | "listing_shared"
  | "note_added"
  | "call_logged"
  | "spoke_on_phone"
  | "meeting_scheduled"
  | "task_created"
  | "task_completed"
  | "tour_scheduled"
  | "tour_completed"
  | "invoice_sent"
  | "payment_received"
  | "status_changed"
  | "lease_uploaded"
  | "gift_card_sent";

interface Activity {
  id: string;
  user_id: string;
  activity_type: ActivityType;
  entity_type: string;
  entity_id?: string;
  metadata: Record<string, any>;
  created_at: string;
  user?: Agent;
}

interface Client {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface Agent {
  id: string;
  full_name: string;
  email: string;
  avatar_url?: string;
}

interface ClientActivitySummary {
  client: Client;
  activityCount: number;
  lastActivity?: Activity;
  recentTypes: ActivityType[];
}

// ==================== Centralized Activity Configuration ====================
// Synced with ApartmentDashboard.tsx for consistency

const ACTIVITY_CONFIG: Record<
  string,
  { icon: string; color: string; label: string; category: string }
> = {
  // Client Management
  client_created: {
    icon: "👤",
    color: "#4CAF50",
    label: "Created client",
    category: "clients",
  },
  client_updated: {
    icon: "✏️",
    color: "#2196F3",
    label: "Updated client",
    category: "clients",
  },
  client_deleted: {
    icon: "🗑️",
    color: "#f44336",
    label: "Deleted client",
    category: "clients",
  },
  status_changed: {
    icon: "🔄",
    color: "#2196F3",
    label: "Changed status",
    category: "clients",
  },

  // Communication
  call_logged: {
    icon: "📞",
    color: "#009688",
    label: "Logged call",
    category: "communication",
  },
  spoke_on_phone: {
    icon: "📞",
    color: "#009688",
    label: "Spoke on phone",
    category: "communication",
  },
  email_sent: {
    icon: "📧",
    color: "#00BCD4",
    label: "Sent email",
    category: "communication",
  },
  emailed: {
    icon: "📧",
    color: "#00BCD4",
    label: "Sent email",
    category: "communication",
  },
  sent_text: {
    icon: "💬",
    color: "#9C27B0",
    label: "Sent text",
    category: "communication",
  },
  left_voicemail: {
    icon: "📱",
    color: "#FF9800",
    label: "Left voicemail",
    category: "communication",
  },
  note_added: {
    icon: "📝",
    color: "#795548",
    label: "Added note",
    category: "communication",
  },

  // Scheduling
  meeting_scheduled: {
    icon: "📅",
    color: "#E91E63",
    label: "Scheduled meeting",
    category: "scheduling",
  },
  tour_scheduled: {
    icon: "🚶",
    color: "#00BCD4",
    label: "Scheduled tour",
    category: "scheduling",
  },
  tour_completed: {
    icon: "🏁",
    color: "#8BC34A",
    label: "Completed tour",
    category: "scheduling",
  },

  // Tasks
  task_created: {
    icon: "✅",
    color: "#FF5722",
    label: "Created task",
    category: "tasks",
  },
  task_completed: {
    icon: "🎉",
    color: "#4CAF50",
    label: "Completed task",
    category: "tasks",
  },

  // Properties & Listings
  property_viewed: {
    icon: "🏠",
    color: "#607D8B",
    label: "Viewed property",
    category: "properties",
  },
  listing_shared: {
    icon: "📤",
    color: "#3F51B5",
    label: "Shared listings",
    category: "properties",
  },
  guest_card_sent: {
    icon: "📋",
    color: "#FF9800",
    label: "Sent guest card",
    category: "properties",
  },
  guest_card_viewed: {
    icon: "👁️",
    color: "#8BC34A",
    label: "Guest card viewed",
    category: "properties",
  },

  // Search
  search_executed: {
    icon: "🔍",
    color: "#9C27B0",
    label: "Executed search",
    category: "search",
  },
  search_saved: {
    icon: "💾",
    color: "#673AB7",
    label: "Saved search",
    category: "search",
  },

  // Financial
  invoice_sent: {
    icon: "💵",
    color: "#FFC107",
    label: "Sent invoice",
    category: "financial",
  },
  payment_received: {
    icon: "💰",
    color: "#4CAF50",
    label: "Payment received",
    category: "financial",
  },
  gift_card_sent: {
    icon: "🎁",
    color: "#E91E63",
    label: "Sent gift card",
    category: "financial",
  },

  // Documents
  lease_uploaded: {
    icon: "📄",
    color: "#607D8B",
    label: "Uploaded lease",
    category: "documents",
  },
};

const ACTIVITY_CATEGORIES = [
  { key: "all", label: "All Activities" },
  { key: "communication", label: "Communication" },
  { key: "clients", label: "Client Management" },
  { key: "scheduling", label: "Scheduling" },
  { key: "tasks", label: "Tasks" },
  { key: "properties", label: "Properties" },
  { key: "financial", label: "Financial" },
  { key: "search", label: "Search" },
  { key: "documents", label: "Documents" },
];

// ==================== Helpers ====================

const getActivityConfig = (type: string) => {
  return (
    ACTIVITY_CONFIG[type] || {
      icon: "📌",
      color: "#757575",
      label: type.replace(/_/g, " "),
      category: "other",
    }
  );
};

const formatActivityDescription = (activity: Activity): string => {
  const { activity_type, metadata } = activity;
  const clientName = metadata?.client_name || "Unknown Client";

  switch (activity_type) {
    case "client_created":
      return `Created new client: ${clientName}`;
    case "client_updated":
      return `Updated client: ${clientName}`;
    case "client_deleted":
      return `Deleted client: ${clientName}`;
    case "status_changed":
      return `Changed status for ${clientName}${
        metadata?.new_status ? ` to ${metadata.new_status}` : ""
      }`;
    case "call_logged":
    case "spoke_on_phone":
      return `Call with ${clientName}${
        metadata?.outcome ? ` - ${metadata.outcome}` : ""
      }`;
    case "email_sent":
    case "emailed":
      return `Email to ${clientName}${
        metadata?.subject ? `: "${metadata.subject}"` : ""
      }`;
    case "sent_text":
      return `Text message to ${clientName}`;
    case "left_voicemail":
      return `Left voicemail for ${clientName}`;
    case "search_executed":
      return `Searched ${metadata?.result_count || 0} properties`;
    case "search_saved":
      return `Saved search "${metadata?.search_name || "Untitled"}"`;
    case "guest_card_sent":
      return `Sent guest card for ${
        metadata?.property_name || "property"
      } to ${clientName}`;
    case "guest_card_viewed":
      return `Guest card viewed by ${clientName}`;
    case "property_viewed":
      return `Viewed property: ${metadata?.property_name || "Unknown"}`;
    case "listing_shared":
      return `Shared ${metadata?.unit_count || ""} listings with ${clientName}`;
    case "note_added":
      return `Added note for ${clientName}`;
    case "task_created":
      return `Created task "${
        metadata?.task_title || "Untitled"
      }" for ${clientName}`;
    case "task_completed":
      return `Completed task "${
        metadata?.task_title || "Untitled"
      }" for ${clientName}`;
    case "meeting_scheduled":
      return `Scheduled ${
        metadata?.meeting_type || "meeting"
      } with ${clientName}`;
    case "tour_scheduled":
      return `Scheduled tour for ${clientName}`;
    case "tour_completed":
      return `Completed tour for ${clientName}`;
    case "invoice_sent":
      return `Sent invoice to ${clientName}`;
    case "payment_received":
      return `Received payment from ${clientName}`;
    case "lease_uploaded":
      return `Uploaded lease for ${clientName}`;
    case "gift_card_sent":
      return `Sent gift card to ${clientName}`;
    default:
      return `${getActivityConfig(activity_type).label} - ${clientName}`;
  }
};

const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const formatFullDateTime = (dateString: string): string => {
  return new Date(dateString).toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const getDateGroupLabel = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const thisWeekStart = new Date(today);
  thisWeekStart.setDate(thisWeekStart.getDate() - today.getDay());
  const lastWeekStart = new Date(thisWeekStart);
  lastWeekStart.setDate(lastWeekStart.getDate() - 7);

  const activityDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  if (activityDate.getTime() === today.getTime()) return "Today";
  if (activityDate.getTime() === yesterday.getTime()) return "Yesterday";
  if (activityDate >= thisWeekStart) return "This Week";
  if (activityDate >= lastWeekStart) return "Last Week";
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

// Activity type options for filtering (organized by category)
const activityTypeOptions = Object.entries(ACTIVITY_CONFIG).map(
  ([value, config]) => ({
    value: value as ActivityType,
    label: config.label,
    category: config.category,
    icon: config.icon,
  })
);

// Report period options
const reportPeriods = [
  { value: "week", label: "This Week", days: 7 },
  { value: "month", label: "This Month", days: 30 },
  { value: "quarter", label: "This Quarter", days: 90 },
  { value: "year", label: "This Year", days: 365 },
  { value: "custom", label: "Custom Range", days: 0 },
];

// ==================== Sub-Components ====================

// Activity Item for Timeline/List View
interface ActivityItemProps {
  activity: Activity;
  showDate?: boolean;
  onClick: () => void;
  onQuickAction?: (action: string, activity: Activity) => void;
}

const ActivityItem: React.FC<ActivityItemProps> = ({
  activity,
  showDate = false,
  onClick,
  onQuickAction,
}) => {
  const theme = useTheme();
  const config = getActivityConfig(activity.activity_type);
  const [hovered, setHovered] = useState(false);

  return (
    <Box
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: "flex",
        gap: 2,
        py: 1.5,
        px: 2,
        borderRadius: 2,
        cursor: "pointer",
        transition: "all 0.2s ease",
        "&:hover": {
          backgroundColor: alpha(config.color, 0.08),
          transform: "translateX(4px)",
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: alpha(config.color, 0.15),
          fontSize: "1.2rem",
          flexShrink: 0,
        }}
      >
        {config.icon}
      </Box>
      <Box flex={1} minWidth={0}>
        <Typography variant="body2" fontWeight={500} noWrap>
          {formatActivityDescription(activity)}
        </Typography>
        <Box display="flex" alignItems="center" gap={1} mt={0.25}>
          <Typography variant="caption" color="text.secondary">
            {showDate
              ? formatFullDateTime(activity.created_at)
              : formatRelativeTime(activity.created_at)}
          </Typography>
          {activity.user && (
            <>
              <Box
                sx={{
                  width: 3,
                  height: 3,
                  borderRadius: "50%",
                  backgroundColor: "text.disabled",
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {activity.user.full_name?.split(" ")[0] || "Agent"}
              </Typography>
            </>
          )}
        </Box>
      </Box>

      {/* Quick Actions on Hover */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          opacity: hovered ? 1 : 0,
          transition: "opacity 0.2s",
        }}
      >
        {activity.entity_id && activity.entity_type === "client" && (
          <Tooltip title="View Client">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onQuickAction?.("view_client", activity);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {["call_logged", "spoke_on_phone"].includes(activity.activity_type) && (
          <Tooltip title="Call Again">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onQuickAction?.("call", activity);
              }}
            >
              <PhoneIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {["email_sent", "emailed"].includes(activity.activity_type) && (
          <Tooltip title="Send Another Email">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onQuickAction?.("email", activity);
              }}
            >
              <EmailIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <ArrowForwardIcon
        sx={{
          color: "text.disabled",
          fontSize: 18,
          alignSelf: "center",
          opacity: hovered ? 1 : 0.5,
        }}
      />
    </Box>
  );
};

// Date Group Header
interface DateGroupHeaderProps {
  label: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
}

const DateGroupHeader: React.FC<DateGroupHeaderProps> = ({
  label,
  count,
  expanded,
  onToggle,
}) => {
  const theme = useTheme();

  return (
    <Box
      onClick={onToggle}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 1.5,
        px: 2,
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
        borderRadius: 1,
        cursor: "pointer",
        mb: 1,
        "&:hover": {
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
        },
      }}
    >
      <Box display="flex" alignItems="center" gap={1.5}>
        <TodayIcon sx={{ fontSize: 18, color: "text.secondary" }} />
        <Typography variant="subtitle2" fontWeight={600}>
          {label}
        </Typography>
        <Chip
          size="small"
          label={count}
          sx={{
            height: 20,
            fontSize: "0.7rem",
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
          }}
        />
      </Box>
      {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
    </Box>
  );
};

// Client Activity Card for "By Client" tab
interface ClientActivityCardProps {
  summary: ClientActivitySummary;
  onClick: () => void;
}

const ClientActivityCard: React.FC<ClientActivityCardProps> = ({
  summary,
  onClick,
}) => {
  const theme = useTheme();
  const { client, activityCount, lastActivity, recentTypes } = summary;

  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: "pointer",
        transition: "all 0.2s",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: shadows.cardHover,
        },
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" gap={2} mb={1.5}>
          <Avatar sx={{ width: 44, height: 44, background: gradients.primary }}>
            {client.first_name?.[0]}
            {client.last_name?.[0]}
          </Avatar>
          <Box flex={1} minWidth={0}>
            <Typography variant="body1" fontWeight={600} noWrap>
              {client.first_name} {client.last_name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {client.email}
            </Typography>
          </Box>
          <Badge
            badgeContent={activityCount}
            color="primary"
            max={99}
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "0.7rem",
                height: 20,
                minWidth: 20,
              },
            }}
          >
            <TrendingUpIcon sx={{ color: "text.secondary" }} />
          </Badge>
        </Box>

        {/* Recent Activity Types */}
        <Box display="flex" gap={0.5} flexWrap="wrap" mb={1}>
          {recentTypes.slice(0, 4).map((type, i) => {
            const config = getActivityConfig(type);
            return (
              <Tooltip key={i} title={config.label}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: alpha(config.color, 0.15),
                    fontSize: "0.8rem",
                  }}
                >
                  {config.icon}
                </Box>
              </Tooltip>
            );
          })}
          {recentTypes.length > 4 && (
            <Chip
              size="small"
              label={`+${recentTypes.length - 4}`}
              sx={{ height: 24, fontSize: "0.7rem" }}
            />
          )}
        </Box>

        {/* Last Activity */}
        {lastActivity && (
          <Typography variant="caption" color="text.secondary">
            Last: {formatRelativeTime(lastActivity.created_at)}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

// Stat Mini Card for Analytics
interface StatMiniCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  change?: number;
}

const StatMiniCard: React.FC<StatMiniCardProps> = ({
  title,
  value,
  icon,
  color,
  change,
}) => {
  const theme = useTheme();

  return (
    <Paper
      sx={{
        p: 2,
        backgroundColor: alpha(color, 0.08),
        border: `1px solid ${alpha(color, 0.2)}`,
      }}
    >
      <Box display="flex" alignItems="center" gap={2}>
        <Avatar
          sx={{
            width: 44,
            height: 44,
            backgroundColor: alpha(color, 0.15),
            color: color,
          }}
        >
          {icon}
        </Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {typeof value === "number" ? value.toLocaleString() : value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {title}
          </Typography>
        </Box>
        {change !== undefined && (
          <Chip
            size="small"
            label={`${change >= 0 ? "+" : ""}${change}%`}
            sx={{
              ml: "auto",
              backgroundColor: alpha(
                change >= 0
                  ? theme.palette.success.main
                  : theme.palette.error.main,
                0.15
              ),
              color:
                change >= 0
                  ? theme.palette.success.main
                  : theme.palette.error.main,
              fontWeight: 600,
            }}
          />
        )}
      </Box>
    </Paper>
  );
};

// ==================== Main Component ====================

interface ActivityCenterProps {
  onClose?: () => void;
  initialClientId?: string;
  onNavigate?: (page: string, clientId?: string) => void;
}

const ActivityCenter: React.FC<ActivityCenterProps> = ({
  onClose,
  initialClientId,
  onNavigate,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // View mode
  const [viewMode, setViewMode] = useState<"list" | "timeline">("timeline");

  // Activity list state
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<ActivityType[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Clients for filter dropdown
  const [clients, setClients] = useState<Client[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);

  // Client activity summaries for "By Client" tab
  const [clientSummaries, setClientSummaries] = useState<
    ClientActivitySummary[]
  >([]);
  const [clientSummariesLoading, setClientSummariesLoading] = useState(false);

  // Analytics state
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsPeriod, setAnalyticsPeriod] = useState<"7d" | "30d" | "90d">(
    "30d"
  );

  // Report state
  const [reportPeriod, setReportPeriod] = useState("month");
  const [reportData, setReportData] = useState<any>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  // Date group expansion state
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {
      Today: true,
      Yesterday: true,
      "This Week": true,
    }
  );

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load clients and agents for filters
  useEffect(() => {
    loadClients();
    loadAgents();
  }, []);

  // Load activities when filters change
  useEffect(() => {
    loadActivities();
  }, [
    page,
    rowsPerPage,
    selectedTypes,
    selectedClient,
    startDate,
    endDate,
    debouncedSearch,
    selectedCategory,
  ]);

  // Set initial client if provided
  useEffect(() => {
    if (initialClientId && clients.length > 0) {
      const client = clients.find((c) => c.id === initialClientId);
      if (client) setSelectedClient(client);
    }
  }, [initialClientId, clients]);

  // Load analytics when tab changes or period changes
  useEffect(() => {
    if (activeTab === 2) {
      loadAnalytics();
    }
  }, [activeTab, analyticsPeriod]);

  // Load client summaries when "By Client" tab is active
  useEffect(() => {
    if (activeTab === 1) {
      loadClientSummaries();
    }
  }, [activeTab]);

  const loadClients = async () => {
    try {
      const { data, error } = await supabase
        .from("clients")
        .select("id, first_name, last_name, email")
        .order("first_name");

      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error("Error loading clients:", error);
    }
  };

  const loadAgents = async () => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, email, avatar_url")
        .order("full_name");

      if (error) throw error;
      setAgents(data || []);
    } catch (error) {
      console.error("Error loading agents:", error);
    }
  };

  const loadActivities = async () => {
    setLoading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setActivities([]);
        setTotalCount(0);
        return;
      }

      let query = supabase
        .from("activities")
        .select("*", { count: "exact" })
        .eq("user_id", user.id);

      // Date filters
      if (startDate) {
        query = query.gte("created_at", startDate);
      }
      if (endDate) {
        query = query.lte("created_at", endDate + "T23:59:59");
      }

      // Type filters
      if (selectedTypes.length > 0) {
        query = query.in("activity_type", selectedTypes);
      } else if (selectedCategory !== "all") {
        const categoryTypes = Object.entries(ACTIVITY_CONFIG)
          .filter(([_, config]) => config.category === selectedCategory)
          .map(([type]) => type);
        if (categoryTypes.length > 0) {
          query = query.in("activity_type", categoryTypes);
        }
      }

      // Client filter
      if (selectedClient) {
        query = query.eq("entity_id", selectedClient.id);
      }

      // Server-side search in metadata (if supported)
      // For now we'll do client-side filtering for search

      query = query
        .order("created_at", { ascending: false })
        .range(page * rowsPerPage, (page + 1) * rowsPerPage - 1);

      const { data, error, count } = await query;

      if (error) throw error;

      // Attach agent info
      let enrichedActivities = (data || []).map((a) => ({
        ...a,
        user: agents.find((agent) => agent.id === a.user_id),
      }));

      // Client-side search filter
      if (debouncedSearch) {
        const q = debouncedSearch.toLowerCase();
        enrichedActivities = enrichedActivities.filter((a) => {
          const desc = formatActivityDescription(a).toLowerCase();
          const clientName = (a.metadata?.client_name || "").toLowerCase();
          const notes = (a.metadata?.notes || "").toLowerCase();
          return (
            desc.includes(q) || clientName.includes(q) || notes.includes(q)
          );
        });
      }

      setActivities(enrichedActivities);
      setTotalCount(count || 0);
    } catch (error) {
      console.error("Error loading activities:", error);
      setActivities([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const loadClientSummaries = async () => {
    setClientSummariesLoading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      // Get activities from last 30 days grouped by client
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data: activities, error } = await supabase
        .from("activities")
        .select("*")
        .eq("user_id", user.id)
        .eq("entity_type", "client")
        .not("entity_id", "is", null)
        .gte("created_at", thirtyDaysAgo.toISOString())
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Group by client
      const clientMap: Record<string, Activity[]> = {};
      (activities || []).forEach((a) => {
        if (a.entity_id) {
          if (!clientMap[a.entity_id]) {
            clientMap[a.entity_id] = [];
          }
          clientMap[a.entity_id].push(a);
        }
      });

      // Build summaries
      const summaries: ClientActivitySummary[] = [];
      for (const [clientId, clientActivities] of Object.entries(clientMap)) {
        const client = clients.find((c) => c.id === clientId);
        if (client) {
          const uniqueTypes = Array.from(
            new Set(
              clientActivities.map((a) => a.activity_type as ActivityType)
            )
          );
          summaries.push({
            client,
            activityCount: clientActivities.length,
            lastActivity: clientActivities[0],
            recentTypes: uniqueTypes,
          });
        }
      }

      // Sort by activity count
      summaries.sort((a, b) => b.activityCount - a.activityCount);
      setClientSummaries(summaries);
    } catch (error) {
      console.error("Error loading client summaries:", error);
    } finally {
      setClientSummariesLoading(false);
    }
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const days =
        analyticsPeriod === "7d" ? 7 : analyticsPeriod === "30d" ? 30 : 90;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data: activities, error } = await supabase
        .from("activities")
        .select("*")
        .eq("user_id", user.id)
        .gte("created_at", startDate.toISOString())
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Calculate analytics
      const byType: Record<string, number> = {};
      const byCategory: Record<string, number> = {};
      const byDay: Record<string, number> = {};
      const clientSet = new Set<string>();

      (activities || []).forEach((a) => {
        // By type
        byType[a.activity_type] = (byType[a.activity_type] || 0) + 1;

        // By category
        const category = getActivityConfig(a.activity_type).category;
        byCategory[category] = (byCategory[category] || 0) + 1;

        // By day
        const dayKey = new Date(a.created_at).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
        byDay[dayKey] = (byDay[dayKey] || 0) + 1;

        // Unique clients
        if (a.entity_id && a.entity_type === "client") {
          clientSet.add(a.entity_id);
        }
      });

      // Build trend data
      const trendData = [];
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dayKey = date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
        trendData.push({
          date: dayKey,
          activities: byDay[dayKey] || 0,
        });
      }

      // Type chart data
      const typeChartData = Object.entries(byType)
        .map(([type, count]) => ({
          name: getActivityConfig(type).label,
          value: count,
          color: getActivityConfig(type).color,
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 8);

      // Category chart data
      const categoryChartData = Object.entries(byCategory)
        .map(([cat, count]) => ({
          name: cat.charAt(0).toUpperCase() + cat.slice(1),
          value: count,
        }))
        .sort((a, b) => b.value - a.value);

      // Calculate daily average
      const totalActivities = activities?.length || 0;
      const dailyAverage = Math.round(totalActivities / days);

      // Most active day
      const mostActiveDay =
        Object.entries(byDay).sort((a, b) => b[1] - a[1])[0] || null;

      setAnalyticsData({
        totalActivities,
        uniqueClients: clientSet.size,
        dailyAverage,
        mostActiveDay,
        byType,
        byCategory,
        trendData,
        typeChartData,
        categoryChartData,
      });
    } catch (error) {
      console.error("Error loading analytics:", error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    loadActivities();
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setDebouncedSearch("");
    setSelectedTypes([]);
    setSelectedCategory("all");
    setSelectedClient(null);
    setStartDate("");
    setEndDate("");
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleQuickAction = (action: string, activity: Activity) => {
    if (action === "view_client" && activity.entity_id) {
      onNavigate?.("clients", activity.entity_id);
      onClose?.();
    }
    // Add more quick actions as needed
  };

  const handleClientSelect = (client: Client) => {
    setSelectedClient(client);
    setActiveTab(0);
    setPage(0);
  };

  const toggleGroupExpanded = (group: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  // Generate report
  const generateReport = async () => {
    setReportLoading(true);
    try {
      let start: Date;
      let end = new Date();

      if (reportPeriod === "custom" && customStartDate && customEndDate) {
        start = new Date(customStartDate);
        end = new Date(customEndDate);
      } else {
        const period = reportPeriods.find((p) => p.value === reportPeriod);
        start = new Date();
        start.setDate(start.getDate() - (period?.days || 30));
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { data: activities, error } = await supabase
        .from("activities")
        .select("*")
        .eq("user_id", user.id)
        .gte("created_at", start.toISOString())
        .lte("created_at", end.toISOString())
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Calculate summary
      const byType: Record<string, number> = {};
      const clientCounts: Record<
        string,
        { clientName: string; count: number }
      > = {};

      (activities || []).forEach((a) => {
        byType[a.activity_type] = (byType[a.activity_type] || 0) + 1;

        if (a.entity_id && a.metadata?.client_name) {
          if (!clientCounts[a.entity_id]) {
            clientCounts[a.entity_id] = {
              clientName: a.metadata.client_name,
              count: 0,
            };
          }
          clientCounts[a.entity_id].count++;
        }
      });

      const typeChartData = Object.entries(byType).map(([type, count]) => ({
        name: getActivityConfig(type).label,
        value: count,
        color: getActivityConfig(type).color,
      }));

      const byClient = Object.entries(clientCounts)
        .map(([clientId, data]) => ({ clientId, ...data }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 20);

      const clientChartData = byClient.slice(0, 10).map((c) => ({
        name: c.clientName,
        activities: c.count,
      }));

      setReportData({
        totalActivities: activities?.length || 0,
        byType,
        byClient,
        typeChartData,
        clientChartData,
        startDate: start,
        endDate: end,
        activities: activities || [],
      });
    } catch (error) {
      console.error("Error generating report:", error);
    } finally {
      setReportLoading(false);
    }
  };

  // Export report as CSV
  const exportCSV = () => {
    if (!reportData) return;

    const headers = [
      "Date",
      "Time",
      "Activity Type",
      "Category",
      "Client",
      "Description",
    ];
    const rows = reportData.activities.map((a: Activity) => [
      new Date(a.created_at).toLocaleDateString(),
      new Date(a.created_at).toLocaleTimeString(),
      getActivityConfig(a.activity_type).label,
      getActivityConfig(a.activity_type).category,
      a.metadata?.client_name || "-",
      formatActivityDescription(a),
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((r: string[]) =>
        r.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `activity-report-${reportPeriod}-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Quick date filters
  const setQuickDateFilter = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setStartDate(start.toISOString().split("T")[0]);
    setEndDate(end.toISOString().split("T")[0]);
    setPage(0);
  };

  // Group activities by date for timeline view
  const groupedActivities = useMemo(() => {
    const groups: Record<string, Activity[]> = {};

    activities.forEach((activity) => {
      const groupLabel = getDateGroupLabel(activity.created_at);
      if (!groups[groupLabel]) {
        groups[groupLabel] = [];
      }
      groups[groupLabel].push(activity);
    });

    return groups;
  }, [activities]);

  const activeFilterCount = [
    selectedTypes.length > 0,
    selectedCategory !== "all",
    selectedClient !== null,
    startDate !== "",
    endDate !== "",
    debouncedSearch !== "",
  ].filter(Boolean).length;

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <PageHeaderTitle
            icon={<TimelineIcon />}
            title="Activity Center"
            subtitle="View, search, and analyze all activities"
            titleVariant="h5"
          />
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ReportIcon />}
              onClick={() => setReportDialogOpen(true)}
            >
              Generate Report
            </Button>
            <PageRefreshButton loading={loading} onClick={loadActivities} size="small" />
            {onClose && (
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            )}
          </Stack>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ px: 3, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="All Activities" />
        <Tab label="By Client" />
        <Tab label="Analytics" />
      </Tabs>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
        {activeTab === 0 && (
          <>
            {/* Search and Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Search activities, clients, notes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={selectedCategory}
                      label="Category"
                      onChange={(e) => {
                        setSelectedCategory(e.target.value);
                        setSelectedTypes([]);
                        setPage(0);
                      }}
                    >
                      {ACTIVITY_CATEGORIES.map((cat) => (
                        <MenuItem key={cat.key} value={cat.key}>
                          {cat.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Button
                    fullWidth
                    variant={showFilters ? "contained" : "outlined"}
                    startIcon={<FilterIcon />}
                    onClick={() => setShowFilters(!showFilters)}
                    color={activeFilterCount > 0 ? "primary" : "inherit"}
                  >
                    Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
                  </Button>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <ToggleButtonGroup
                      value={viewMode}
                      exclusive
                      onChange={(_, v) => v && setViewMode(v)}
                      size="small"
                    >
                      <ToggleButton value="timeline">
                        <Tooltip title="Timeline View">
                          <TimelineIcon fontSize="small" />
                        </Tooltip>
                      </ToggleButton>
                      <ToggleButton value="list">
                        <Tooltip title="Table View">
                          <ListViewIcon fontSize="small" />
                        </Tooltip>
                      </ToggleButton>
                    </ToggleButtonGroup>
                    <Divider orientation="vertical" flexItem />
                    <Chip
                      label="Today"
                      size="small"
                      onClick={() => setQuickDateFilter(0)}
                      variant="outlined"
                    />
                    <Chip
                      label="7 Days"
                      size="small"
                      onClick={() => setQuickDateFilter(7)}
                      variant="outlined"
                    />
                    <Chip
                      label="30 Days"
                      size="small"
                      onClick={() => setQuickDateFilter(30)}
                      variant="outlined"
                    />
                    <Chip
                      label="All"
                      size="small"
                      onClick={() => {
                        setStartDate("");
                        setEndDate("");
                        setPage(0);
                      }}
                      variant="outlined"
                    />
                  </Stack>
                </Grid>
              </Grid>

              {/* Expanded Filters */}
              <Collapse in={showFilters}>
                <Box
                  sx={{
                    mt: 3,
                    pt: 2,
                    borderTop: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <Autocomplete
                        multiple
                        options={activityTypeOptions}
                        groupBy={(option) =>
                          option.category.charAt(0).toUpperCase() +
                          option.category.slice(1)
                        }
                        getOptionLabel={(option) => option.label}
                        value={activityTypeOptions.filter((o) =>
                          selectedTypes.includes(o.value)
                        )}
                        onChange={(_, newValue) => {
                          setSelectedTypes(newValue.map((v) => v.value));
                          setSelectedCategory("all");
                          setPage(0);
                        }}
                        renderOption={(props, option) => (
                          <li {...props}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <span>{option.icon}</span>
                              <span>{option.label}</span>
                            </Box>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Activity Types"
                            size="small"
                          />
                        )}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Autocomplete
                        options={clients}
                        getOptionLabel={(option) =>
                          `${option.first_name} ${option.last_name}`
                        }
                        value={selectedClient}
                        onChange={(_, newValue) => {
                          setSelectedClient(newValue);
                          setPage(0);
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Client" size="small" />
                        )}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Start Date"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          setPage(0);
                        }}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField
                        fullWidth
                        type="date"
                        label="End Date"
                        value={endDate}
                        onChange={(e) => {
                          setEndDate(e.target.value);
                          setPage(0);
                        }}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Button
                        fullWidth
                        variant="text"
                        onClick={handleClearFilters}
                        sx={{ height: "100%" }}
                      >
                        Clear All
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Collapse>
            </Paper>

            {/* Activity Display */}
            {loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : activities.length === 0 ? (
              <Paper sx={{ p: 8, textAlign: "center" }}>
                <ScheduleIcon
                  sx={{
                    fontSize: 80,
                    color: "text.secondary",
                    opacity: 0.3,
                    mb: 2,
                  }}
                />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  No activities found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try adjusting your filters or date range
                </Typography>
              </Paper>
            ) : viewMode === "timeline" ? (
              // Timeline View with Date Grouping
              <Paper sx={{ overflow: "hidden" }}>
                {Object.entries(groupedActivities).map(
                  ([group, groupActivities]) => (
                    <Box key={group}>
                      <DateGroupHeader
                        label={group}
                        count={groupActivities.length}
                        expanded={expandedGroups[group] !== false}
                        onToggle={() => toggleGroupExpanded(group)}
                      />
                      <Collapse in={expandedGroups[group] !== false}>
                        <Stack
                          divider={
                            <Divider
                              sx={{ ml: 7, borderColor: alpha("#fff", 0.05) }}
                            />
                          }
                        >
                          {groupActivities.map((activity) => (
                            <ActivityItem
                              key={activity.id}
                              activity={activity}
                              showDate={group !== "Today"}
                              onClick={() => {
                                /* Could open detail dialog */
                              }}
                              onQuickAction={handleQuickAction}
                            />
                          ))}
                        </Stack>
                      </Collapse>
                    </Box>
                  )
                )}
                <TablePagination
                  rowsPerPageOptions={[25, 50, 100]}
                  component="div"
                  count={totalCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Paper>
            ) : (
              // Table View
              <Paper>
                {isMobile ? (
                  <Box>
                    {activities.map((activity, idx, arr) => {
                      const config = getActivityConfig(activity.activity_type);
                      const when = new Date(activity.created_at);
                      const clientName = activity.metadata?.client_name as
                        | string
                        | undefined;
                      const captionBits: string[] = [];
                      if (clientName) captionBits.push(clientName);
                      captionBits.push(
                        when.toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                        })
                      );
                      captionBits.push(
                        when.toLocaleTimeString([], {
                          hour: "numeric",
                          minute: "2-digit",
                        })
                      );
                      return (
                        <Box
                          key={activity.id}
                          onClick={() => {
                            if (clientName && activity.entity_id) {
                              onNavigate?.("clients", activity.entity_id);
                              onClose?.();
                            }
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.25,
                            px: 1.5,
                            py: 1.25,
                            cursor: clientName ? "pointer" : "default",
                            borderBottom:
                              idx < arr.length - 1
                                ? `1px solid ${alpha(theme.palette.divider, 0.6)}`
                                : "none",
                            "&:hover": clientName
                              ? {
                                  backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    0.04
                                  ),
                                }
                              : undefined,
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: alpha(config.color, 0.15),
                              fontSize: "1rem",
                              flexShrink: 0,
                            }}
                          >
                            {config.icon}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              noWrap
                            >
                              {config.label}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                            >
                              {captionBits.join(" \u00b7 ")}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            label={
                              config.category.charAt(0).toUpperCase() +
                              config.category.slice(1)
                            }
                            sx={{
                              flexShrink: 0,
                              height: 22,
                              fontSize: "0.7rem",
                              backgroundColor: alpha(config.color, 0.12),
                              color: config.color,
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell width={50}></TableCell>
                          <TableCell>Activity</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell>Client</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Time</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {activities.map((activity) => {
                          const config = getActivityConfig(
                            activity.activity_type
                          );
                          return (
                            <TableRow key={activity.id} hover>
                              <TableCell>
                                <Box
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: alpha(config.color, 0.15),
                                    fontSize: "1.2rem",
                                  }}
                                >
                                  {config.icon}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>
                                  {config.label}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {formatActivityDescription(activity)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={
                                    config.category.charAt(0).toUpperCase() +
                                    config.category.slice(1)
                                  }
                                  sx={{
                                    backgroundColor: alpha(config.color, 0.1),
                                    color: config.color,
                                    fontSize: "0.7rem",
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                {activity.metadata?.client_name ? (
                                  <Chip
                                    size="small"
                                    label={activity.metadata.client_name}
                                    variant="outlined"
                                    icon={<PersonIcon />}
                                    onClick={() => {
                                      if (activity.entity_id) {
                                        onNavigate?.(
                                          "clients",
                                          activity.entity_id
                                        );
                                        onClose?.();
                                      }
                                    }}
                                    sx={{ cursor: "pointer" }}
                                  />
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    -
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {new Date(
                                    activity.created_at
                                  ).toLocaleDateString()}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {new Date(
                                    activity.created_at
                                  ).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  component="div"
                  count={totalCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Paper>
            )}
          </>
        )}

        {activeTab === 1 && (
          <Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
            >
              <Box>
                <Typography variant="h6" gutterBottom>
                  Activity by Client
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  See which clients you've been most active with (last 30 days)
                </Typography>
              </Box>
              <Autocomplete
                options={clients}
                getOptionLabel={(option) =>
                  `${option.first_name} ${option.last_name} (${option.email})`
                }
                value={selectedClient}
                onChange={(_, newValue) => {
                  if (newValue) handleClientSelect(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search clients..."
                    size="small"
                    sx={{ width: 300 }}
                  />
                )}
              />
            </Box>

            {clientSummariesLoading ? (
              <Grid container spacing={2}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <Grid item xs={12} sm={6} md={3} key={i}>
                    <Skeleton
                      variant="rectangular"
                      height={140}
                      sx={{ borderRadius: 2 }}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : clientSummaries.length > 0 ? (
              <Grid container spacing={2}>
                {clientSummaries.map((summary) => (
                  <Grid item xs={12} sm={6} md={3} key={summary.client.id}>
                    <ClientActivityCard
                      summary={summary}
                      onClick={() => handleClientSelect(summary.client)}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Paper sx={{ p: 6, textAlign: "center" }}>
                <PersonIcon
                  sx={{
                    fontSize: 60,
                    color: "text.secondary",
                    opacity: 0.3,
                    mb: 2,
                  }}
                />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No client activity found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Start logging activities with clients to see them here
                </Typography>
              </Paper>
            )}
          </Box>
        )}

        {activeTab === 2 && (
          <Box>
            {/* Analytics Period Selector */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
            >
              <Box>
                <Typography variant="h6" gutterBottom>
                  Activity Analytics
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Track your productivity and engagement patterns
                </Typography>
              </Box>
              <Stack direction="row" spacing={2} alignItems="center">
                <ToggleButtonGroup
                  value={analyticsPeriod}
                  exclusive
                  onChange={(_, v) => v && setAnalyticsPeriod(v)}
                  size="small"
                >
                  <ToggleButton value="7d">7 Days</ToggleButton>
                  <ToggleButton value="30d">30 Days</ToggleButton>
                  <ToggleButton value="90d">90 Days</ToggleButton>
                </ToggleButtonGroup>
                <Button
                  variant="contained"
                  startIcon={<ReportIcon />}
                  onClick={() => setReportDialogOpen(true)}
                >
                  Full Report
                </Button>
              </Stack>
            </Box>

            {analyticsLoading ? (
              <Grid container spacing={3}>
                {Array.from({ length: 4 }).map((_, i) => (
                  <Grid item xs={6} md={3} key={i}>
                    <Skeleton
                      variant="rectangular"
                      height={100}
                      sx={{ borderRadius: 2 }}
                    />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Skeleton
                    variant="rectangular"
                    height={300}
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>
              </Grid>
            ) : analyticsData ? (
              <>
                {/* Summary Stats */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} md={3}>
                    <StatMiniCard
                      title="Total Activities"
                      value={analyticsData.totalActivities}
                      icon={<TrendingUpIcon />}
                      color={theme.palette.primary.main}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <StatMiniCard
                      title="Unique Clients"
                      value={analyticsData.uniqueClients}
                      icon={<PersonIcon />}
                      color="#10B981"
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <StatMiniCard
                      title="Daily Average"
                      value={analyticsData.dailyAverage}
                      icon={<CalendarIcon />}
                      color="#8B5CF6"
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <StatMiniCard
                      title="Most Active Day"
                      value={analyticsData.mostActiveDay?.[0] || "-"}
                      icon={<TodayIcon />}
                      color="#F59E0B"
                    />
                  </Grid>
                </Grid>

                {/* Charts */}
                <Grid container spacing={3}>
                  {/* Activity Trend */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        gutterBottom
                      >
                        Activity Trend
                      </Typography>
                      <ResponsiveContainer width="100%" height={250}>
                        <AreaChart data={analyticsData.trendData}>
                          <defs>
                            <linearGradient
                              id="colorActivities"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor={theme.palette.primary.main}
                                stopOpacity={0.3}
                              />
                              <stop
                                offset="95%"
                                stopColor={theme.palette.primary.main}
                                stopOpacity={0}
                              />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={alpha("#fff", 0.05)}
                          />
                          <XAxis
                            dataKey="date"
                            stroke={theme.palette.text.secondary}
                            fontSize={12}
                          />
                          <YAxis
                            stroke={theme.palette.text.secondary}
                            fontSize={12}
                          />
                          <RechartsTooltip
                            contentStyle={{
                              backgroundColor: theme.palette.background.paper,
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 8,
                            }}
                          />
                          <Area
                            type="monotone"
                            dataKey="activities"
                            stroke={theme.palette.primary.main}
                            fillOpacity={1}
                            fill="url(#colorActivities)"
                            strokeWidth={2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>

                  {/* Activity by Type (Pie) */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, height: "100%" }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        gutterBottom
                      >
                        Activities by Type
                      </Typography>
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={analyticsData.typeChartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {analyticsData.typeChartData.map(
                              (entry: any, index: number) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={entry.color}
                                />
                              )
                            )}
                          </Pie>
                          <RechartsTooltip
                            contentStyle={{
                              backgroundColor: theme.palette.background.paper,
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 8,
                            }}
                          />
                          <Legend
                            formatter={(value) => (
                              <span
                                style={{ color: theme.palette.text.primary }}
                              >
                                {value}
                              </span>
                            )}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>

                  {/* Activity by Category (Bar) */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, height: "100%" }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        gutterBottom
                      >
                        Activities by Category
                      </Typography>
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart
                          data={analyticsData.categoryChartData}
                          layout="vertical"
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={alpha("#fff", 0.05)}
                          />
                          <XAxis
                            type="number"
                            stroke={theme.palette.text.secondary}
                            fontSize={12}
                          />
                          <YAxis
                            dataKey="name"
                            type="category"
                            width={100}
                            stroke={theme.palette.text.secondary}
                            fontSize={12}
                          />
                          <RechartsTooltip
                            contentStyle={{
                              backgroundColor: theme.palette.background.paper,
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 8,
                            }}
                          />
                          <Bar
                            dataKey="value"
                            fill={theme.palette.primary.main}
                            radius={[0, 4, 4, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                </Grid>
              </>
            ) : (
              <Paper sx={{ p: 6, textAlign: "center" }}>
                <ReportIcon
                  sx={{
                    fontSize: 60,
                    color: "text.secondary",
                    opacity: 0.3,
                    mb: 2,
                  }}
                />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No analytics data
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Start logging activities to see analytics
                </Typography>
              </Paper>
            )}
          </Box>
        )}
      </Box>

      {/* Report Generation Dialog */}
      <Dialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Activity Report</Typography>
            <IconButton onClick={() => setReportDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Report Period Selection */}
          <Paper
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Report Period
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
              {reportPeriods.map((period) => (
                <Chip
                  key={period.value}
                  label={period.label}
                  onClick={() => setReportPeriod(period.value)}
                  color={reportPeriod === period.value ? "primary" : "default"}
                  variant={
                    reportPeriod === period.value ? "filled" : "outlined"
                  }
                />
              ))}
            </Stack>

            {reportPeriod === "custom" && (
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <TextField
                  type="date"
                  label="Start Date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  type="date"
                  label="End Date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            )}

            <Button
              variant="contained"
              onClick={generateReport}
              disabled={
                reportLoading ||
                (reportPeriod === "custom" &&
                  (!customStartDate || !customEndDate))
              }
              sx={{ mt: 2 }}
            >
              {reportLoading ? (
                <CircularProgress size={24} />
              ) : (
                "Generate Report"
              )}
            </Button>
          </Paper>

          {/* Report Results */}
          {reportData && (
            <Box>
              {/* Summary Stats */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    }}
                  >
                    <CardContent>
                      <Typography variant="h3" fontWeight={700}>
                        {reportData.totalActivities}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Activities
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {reportData.startDate.toLocaleDateString()} -{" "}
                        {reportData.endDate.toLocaleDateString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      backgroundColor: alpha(theme.palette.success.main, 0.1),
                    }}
                  >
                    <CardContent>
                      <Typography variant="h3" fontWeight={700}>
                        {reportData.byClient.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Clients Engaged
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      backgroundColor: alpha(theme.palette.info.main, 0.1),
                    }}
                  >
                    <CardContent>
                      <Typography variant="h3" fontWeight={700}>
                        {Object.keys(reportData.byType).length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Activity Types
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Charts */}
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      gutterBottom
                    >
                      Activities by Type
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={reportData.typeChartData}
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {reportData.typeChartData.map(
                            (entry: any, index: number) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            )
                          )}
                        </Pie>
                        <RechartsTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      gutterBottom
                    >
                      Top Clients by Activity
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={reportData.clientChartData}
                        layout="vertical"
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis
                          dataKey="name"
                          type="category"
                          width={100}
                          fontSize={12}
                        />
                        <RechartsTooltip />
                        <Bar
                          dataKey="activities"
                          fill={theme.palette.primary.main}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
              </Grid>

              {/* Activity Breakdown Table */}
              <Paper sx={{ mt: 3, p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Activity Breakdown
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Activity Type</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell align="right">Count</TableCell>
                        <TableCell align="right">Percentage</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(reportData.byType)
                        .sort((a, b) => (b[1] as number) - (a[1] as number))
                        .map(([type, count]) => {
                          const config = getActivityConfig(type);
                          return (
                            <TableRow key={type}>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Box
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      borderRadius: "50%",
                                      backgroundColor: alpha(
                                        config.color,
                                        0.15
                                      ),
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: "0.8rem",
                                    }}
                                  >
                                    {config.icon}
                                  </Box>
                                  {config.label}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={
                                    config.category.charAt(0).toUpperCase() +
                                    config.category.slice(1)
                                  }
                                  sx={{
                                    fontSize: "0.7rem",
                                    height: 20,
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                {count as number}
                              </TableCell>
                              <TableCell align="right">
                                {(
                                  ((count as number) /
                                    reportData.totalActivities) *
                                  100
                                ).toFixed(1)}
                                %
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {reportData && (
            <Button startIcon={<CsvIcon />} onClick={exportCSV}>
              Export CSV
            </Button>
          )}
          <Button onClick={() => setReportDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActivityCenter;
